import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { gsrApi, type GsrStatus, type GsrType } from '../features/gsr/api.ts';
import { gsrStatusLabel, gsrStatusPill } from '../features/gsr/status.ts';
import { t } from '../lib/strings.ts';
import { formatDate } from '@/lib/format';
import { Pagination } from '../components/Pagination.tsx';

const PAGE_SIZE = 10;

const STATUS_LABELS: Record<GsrStatus, string> = {
  DRAFT: t.gsr.statusDraft,
  PENDING_SIGNATURE: t.gsr.statusPendingSignature,
  APPROVED: t.gsr.statusApproved,
  RFQ_GENERATED: t.gsr.statusRfqGenerated,
};

const TYPE_LABELS: Record<GsrType, string> = {
  GOODS: t.gsr.typeGoods,
  SERVICE: t.gsr.typeService,
};

type TypeFilter = 'all' | GsrType;

export function Gsrs(): JSX.Element {
  const { data: gsrs = [], isLoading } = useQuery({
    queryKey: ['gsrs'],
    queryFn: () => gsrApi.list(),
  });

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  // Status dropdown includes the raw GSR statuses plus two "pending action" views.
  const [statusFilter, setStatusFilter] = useState<'all' | GsrStatus | 'needsQuotation' | 'awardedNotSent'>('all');
  const [yearFilter, setYearFilter] = useState<string>('all');

  // Distinct years present (by creation date), newest first.
  const years = useMemo(
    () => Array.from(new Set(gsrs.map((g) => new Date(g.createdAt).getFullYear()))).sort((a, b) => b - a),
    [gsrs],
  );

  // Title/number search, debounced so we don't filter per keystroke.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Page resets whenever a filter/search changes (the result set changes).
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [typeFilter, statusFilter, yearFilter, debouncedSearch]);

  const filtered = useMemo(() => gsrs.filter((g) => {
    if (typeFilter !== 'all' && g.type !== typeFilter) return false;
    if (statusFilter === 'needsQuotation') {
      // Ready to send for quotation but not started: GSR approved, quotation not
      // opened yet, and (for goods) the PR created + signed. Service GSRs have no
      // PR, so GSR-approved is enough.
      const prReady = g.type === 'SERVICE' || g.purchaseRequest?.status === 'APPROVED';
      if (g.status !== 'APPROVED' || !prReady) return false;
    } else if (statusFilter === 'awardedNotSent') {
      // RFQ awarded but the bill hasn't been sent to Finance yet.
      if (!(g.request?.award && g.request.award.sentToFinanceAt == null)) return false;
    } else if (statusFilter === 'RFQ_GENERATED') {
      // "Seeking quotations" = RFQ opened but NOT yet contracted (no award).
      if (g.status !== 'RFQ_GENERATED' || g.request?.award) return false;
    } else if (statusFilter !== 'all' && g.status !== statusFilter) {
      return false;
    }
    if (yearFilter !== 'all' && new Date(g.createdAt).getFullYear() !== Number(yearFilter)) return false;
    if (debouncedSearch) {
      const hay = `${g.gsrNumber} ${g.title ?? ''} ${g.section ?? ''}`.toLowerCase();
      if (!hay.includes(debouncedSearch)) return false;
    }
    return true;
  }), [gsrs, typeFilter, statusFilter, yearFilter, debouncedSearch]);

  const total = filtered.length;
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const typeTabs: { key: TypeFilter; label: string }[] = [
    { key: 'all', label: t.gsr.filterAll },
    { key: 'GOODS', label: t.gsr.typeGoods },
    { key: 'SERVICE', label: t.gsr.typeService },
  ];

  return (
    <div className="page space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="font-serif text-3xl tracking-tight">{t.gsr.title}</h1>
        <Link to="/gsr/new" className="btn-primary">{t.gsr.newGsr}</Link>
      </header>

      {/* Toolbar — M3 segmented type filter + status/year selectors on one side,
          search on the other. Sits on the tinted chrome ground, like WapMeet's
          meetings list. */}
      <div className="rounded-3xl bg-[hsl(var(--chrome))] flex flex-wrap items-center justify-between gap-x-6 gap-y-3 p-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          {/* Goods / service — M3 segmented button. */}
          <div
            className="inline-flex items-center overflow-hidden rounded-full border border-[hsl(var(--border))]"
            role="group"
            aria-label={t.gsr.colType}
          >
            {typeTabs.map((tab, i) => {
              const active = typeFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setTypeFilter(tab.key)}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium transition-colors ${
                    i > 0 ? 'border-s border-[hsl(var(--border))]' : ''
                  } ${
                    active
                      ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
                      : 'text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                  }`}
                >
                  {active && <Icon name="check" size={16} />}
                  {tab.label}
                </button>
              );
            })}
          </div>
          <select
            aria-label={t.gsr.colStatus}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | GsrStatus | 'needsQuotation' | 'awardedNotSent')}
            className="select w-auto rounded-full py-1.5"
          >
            <option value="all">{t.gsr.allStatuses}</option>
            {(Object.keys(STATUS_LABELS) as GsrStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
            <option value="needsQuotation">{t.gsr.pendingNeedsQuotation}</option>
            <option value="awardedNotSent">{t.gsr.pendingAwardedNotSent}</option>
          </select>
          <select
            aria-label={t.gsr.yearLabel}
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="select w-auto rounded-full py-1.5"
          >
            <option value="all">{t.gsr.allYears}</option>
            {years.map((y) => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
          </select>
        </div>
        <div className="relative w-full sm:w-72">
          <Icon name="search" size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.gsr.searchPlaceholder}
            className="input rounded-full py-1.5 ps-9 pe-4"
          />
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          {t.gsr.loading}
        </p>
      ) : (
        <section className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
          {pageItems.map((g) => (
            <Link
              key={g.id}
              to="/gsr/$gsrId"
              params={{ gsrId: g.id }}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-4 transition-colors hover:bg-[hsl(var(--surface-high))] sm:px-5"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium leading-snug">
                  <span>{g.title || g.gsrNumber}</span>
                  <span className="pill pill-neutral shrink-0">{TYPE_LABELS[g.type]}</span>
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {g.title && <span className="mono-meta" dir="ltr">{g.gsrNumber}</span>}
                  {g.section && (
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="layers" size={15} className="shrink-0 text-foreground/40" />
                      {g.section}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="calendar_month" size={15} className="shrink-0 text-foreground/40" />
                    <span className="num">{formatDate(g.createdAt)}</span>
                  </span>
                </div>
              </div>
              <span className={`${gsrStatusPill(g)} shrink-0`}>{gsrStatusLabel(g)}</span>
            </Link>
          ))}
          {total === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">{t.gsr.empty}</p>
          )}
        </section>
      )}

      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
    </div>
  );
}
